/**
 * Which contact a patient must leave, and what the clinic's messages reach
 * them through.
 *
 * Mirrors the senders: an email goes to every patient with an email address,
 * an SMS only to a patient with a phone while the clinic has SMS
 * notifications on (createAppointment, editAppointment, cancelAppointment).
 * The public form and the booking endpoint share contactProblem, so the form
 * refuses exactly what the server would.
 */

/** A public booking left no contact the clinic's confirmation can reach. */
export const CONTACT_REQUIRED = "CONTACT_REQUIRED";

type Contact = {
  email?: string | null;
  phone?: string | null;
  smsEnabled: boolean;
};

/** Whether the clinic's confirmations and changes reach this patient at all. */
export function patientReachable({ email, phone, smsEnabled }: Contact): boolean {
  return !!email?.trim() || (smsEnabled && !!phone?.trim());
}

/**
 * Why a booking fails the contact rule, or null when it passes.
 *
 * With SMS off the confirmation can only go by email, so email is required.
 * With SMS on either one will do — the patient picks, and the clinic pays for
 * an SMS only when the patient chose a phone. Pass normalized values: a phone
 * that does not normalize is no phone. Staff booking on a patient's behalf are
 * not bound, the same as the message and consent rules.
 */
export function contactProblem(
  args: Contact & { publicBooking: boolean }
): typeof CONTACT_REQUIRED | null {
  if (!args.publicBooking) return null;
  return patientReachable(args) ? null : CONTACT_REQUIRED;
}

/** "a", "a a b", "a, b a c" — Slovak lists join the last item with "a". */
function joinSk(items: string[]): string {
  if (items.length <= 1) return items.join("");
  return `${items.slice(0, -1).join(", ")} a ${items[items.length - 1]}`;
}

type ClinicMessaging = {
  smsEnabled: boolean;
  cancellationEnabled: boolean;
  emailRemindersEnabled: boolean;
  smsRemindersEnabled: boolean;
};

/**
 * The note under the public form's contact fields: what the patient gets, and
 * where. Only promises what the clinic has turned on.
 */
export function publicContactHint(c: ClinicMessaging): string {
  if (!c.smsEnabled) {
    const items = ["potvrdenie rezervácie"];
    if (c.cancellationEnabled) items.push("odkaz na zrušenie termínu");
    if (c.emailRemindersEnabled) items.push("pripomienku pred termínom");
    return `Na e-mail vám pošleme ${joinSk(items)}.`;
  }

  const both = c.emailRemindersEnabled && c.smsRemindersEnabled;
  const what =
    "potvrdenie rezervácie" +
    (c.cancellationEnabled ? " s odkazom na zrušenie" : "") +
    (both ? " a pripomienku pred termínom" : "");
  let hint = `Stačí e-mail alebo telefón – ${what} vám pošleme na to, čo vyplníte.`;
  if (c.emailRemindersEnabled && !both) {
    hint += " Pripomienku pred termínom posielame len na e-mail.";
  } else if (c.smsRemindersEnabled && !both) {
    hint += " Pripomienku pred termínom posielame len SMS správou.";
  }
  return hint;
}

/** The note under the admin booking modal's contact fields. */
export function adminContactHint(
  c: Pick<ClinicMessaging, "smsEnabled" | "cancellationEnabled">
): string {
  const missing = c.smsEnabled ? "e-mail ani telefón" : "e-mail";
  const loses = c.cancellationEnabled
    ? "potvrdenie ani možnosť zrušiť termín"
    : "potvrdenie";
  return `Ak nezadáte ${missing}, pacient nedostane ${loses}.`;
}

/**
 * Shown when staff cancel or move an appointment the patient will not be told
 * about, or null when the patient will hear. Names the phone to call when
 * there is one.
 */
export function unreachableNotice(
  args: Contact & { action: "cancel" | "edit" }
): string | null {
  if (patientReachable(args)) return null;
  const about = args.action === "cancel" ? "o zrušení" : "o zmene";
  const phone = args.phone?.trim();
  if (!phone) {
    return `Pacient nemá e-mail ani telefón, ${about} termínu sa nedozvie.`;
  }
  return `Pacient nemá e-mail a SMS správy sú vypnuté, ${about} termínu sa nedozvie. Kontaktujte ho telefonicky: ${phone}`;
}
